import { GeographicInput, useGeographicField } from './GeographicConflict';
import React, { useState, useEffect, useRef, useId } from 'react';
import type { FormState } from '@types';
import { COUNTRIES, US_STATES } from '../../services/locationData';
import { fetchStatesForCountry, fetchCitiesForState } from '../utils/geminiClient';
import useGoogleMaps from './useGoogleMaps';
import { addressSubdivisions } from '../common/addressSubdivisions';
import { useGlobalAssets } from '../context/GlobalAssetContext';
import { assetLocationFields, selectableAssetLocations } from '../utils/assetLocationFields';

interface AddressSelectorProps {
    prefix: string;
    formData: Partial<FormState>;
    onFormChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | { target: { name: string, value: any } }) => void;
    theme?: 'light' | 'dark';
}

const AddressSelector: React.FC<AddressSelectorProps> = ({ prefix, formData, onFormChange, theme = 'light' }) => {
    const { assets } = useGlobalAssets();
    const assetLocations = selectableAssetLocations(assets);
    const [states, setStates] = useState<string[]>([]);
    const [cities, setCities] = useState<string[]>([]);
    const [isLoadingStates, setIsLoadingStates] = useState(false);
    const [isLoadingCities, setIsLoadingCities] = useState(false);
    const [useManualState, setUseManualState] = useState(false);
    const [useManualCity, setUseManualCity] = useState(false);
    const [autocompleteError, setAutocompleteError] = useState('');
    // Tracks whether autocomplete just populated the fields, to show the confirmation pill
    const [autoFilled, setAutoFilled] = useState(false);

    const autocompleteContainerRef = useRef<HTMLDivElement>(null);
    const autocompleteRef = useRef<any>(null);
    const onFormChangeRef = useRef(onFormChange);

    useEffect(() => {
        onFormChangeRef.current = onFormChange;
    }, [onFormChange]);

    const { isLoaded, loadError } = useGoogleMaps();

    const [mode, setMode] = useState<'assets' | 'search' | 'manual'>('assets');
    const [selectedAssetId, setSelectedAssetId] = useState('');
    const activeMode = mode === 'assets' && !assetLocations.length ? 'search' : mode;
    const assetSelectId = useId();

    const countryKey = prefix ? `${prefix}_country` : 'country';
    const stateKey  = prefix ? `${prefix}_state`   : 'state';
    const cityKey   = prefix ? `${prefix}_city`    : 'city';
    const addressKey = prefix ? `${prefix}_address` : 'address';
    const zipKey    = prefix ? `${prefix}_zip`     : 'zip';

    const geographicCountryWarning = useGeographicField(countryKey);
    const selectedCountry = formData[countryKey] as string;
    const selectedState   = formData[stateKey]   as string;

    // Initialize Google Maps PlaceAutocompleteElement (new API replacing deprecated Autocomplete)
    useEffect(() => {
        let placeAutocomplete: any = null;
        let timeoutId: any = null;

        const initAutocomplete = () => {
            if (!autocompleteContainerRef.current) return;

            // Wait for the new PlaceAutocompleteElement to be available
            if (!(window as any).google?.maps?.places?.PlaceAutocompleteElement) {
                if (isLoaded) timeoutId = setTimeout(initAutocomplete, 500);
                return;
            }

            // Clean up any previous instance
            if (autocompleteContainerRef.current.firstChild) {
                autocompleteContainerRef.current.innerHTML = '';
            }

            placeAutocomplete = new (window as any).google.maps.places.PlaceAutocompleteElement({
                requestedLanguage: 'en',
                requestedRegion: 'us',
            });

            // Style the web component to match our theme
            placeAutocomplete.style.width = '100%';
            autocompleteContainerRef.current.appendChild(placeAutocomplete);
            setAutocompleteError('');

            const handlePlaceSelect = async (event: any) => {
                try {
                    const place = event.placePrediction?.toPlace?.();
                    if (!place)
                        throw new Error('No place returned');

                    await place.fetchFields({ fields: ['addressComponents', 'formattedAddress', 'location'] });

                    const components = place.addressComponents || [];
                    let streetNumber = '';
                    let route = '';
                    let cityStr = '';
                    let cityRank = 0;
                    let stateStr = '';
                    let zipStr = '';
                    let countryStr = '';

                    for (const component of components) {
                        const types: string[] = component.types || [];
                        if (types.includes('street_number')) streetNumber = component.longText || '';
                        if (types.includes('route')) route = component.longText || '';
                        if (types.includes('locality') && cityRank < 4) {
                            cityStr = component.longText || '';
                            cityRank = 4;
                        }
                        if (types.includes('postal_town') && cityRank < 3) {
                            cityStr = component.longText || '';
                            cityRank = 3;
                        }
                        if (types.includes('sublocality_level_1') && cityRank < 2) {
                            cityStr = component.longText || '';
                            cityRank = 2;
                        }
                        if (types.includes('administrative_area_level_2') && cityRank < 1) {
                            cityStr = component.longText || '';
                            cityRank = 1;
                        }
                        if (types.includes('administrative_area_level_1')) stateStr = component.longText || '';
                        if (types.includes('postal_code')) zipStr = component.longText || '';
                        if (types.includes('country')) countryStr = component.longText || '';
                    }

                    const fullAddress = `${streetNumber} ${route}`.trim();

                    onFormChangeRef.current({ target: { name: addressKey, value: fullAddress || place.formattedAddress || '' } });
                    if (countryStr) onFormChangeRef.current({ target: { name: countryKey, value: countryStr } });
                    if (stateStr)   onFormChangeRef.current({ target: { name: stateKey,   value: stateStr } });
                    if (cityStr)    onFormChangeRef.current({ target: { name: cityKey,    value: cityStr } });
                    if (zipStr)     onFormChangeRef.current({ target: { name: zipKey,     value: zipStr } });

                    if (place.location) {
                        onFormChangeRef.current({ target: { name: prefix ? `${prefix}_lat` : 'lat', value: place.location.lat() } });
                        onFormChangeRef.current({ target: { name: prefix ? `${prefix}_lng` : 'lng', value: place.location.lng() } });
                    }

                    setAutocompleteError('');
                    setAutoFilled(true);
                    setUseManualCity(true);
                } catch {
                    setAutocompleteError('That location could not be loaded. Please retry or enter it manually.');
                }
            };

            const handleAutocompleteError = () =>
                setAutocompleteError('Destination search is unavailable. Please enter the location manually.');

            placeAutocomplete.addEventListener('gmp-select', handlePlaceSelect);
            placeAutocomplete.addEventListener('gmp-error', handleAutocompleteError);
            autocompleteRef.current = { element: placeAutocomplete, handlePlaceSelect, handleAutocompleteError };
        };

        if (isLoaded && activeMode === 'search') initAutocomplete();

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            if (autocompleteRef.current) {
                autocompleteRef.current.element.removeEventListener('gmp-select', autocompleteRef.current.handlePlaceSelect);
                autocompleteRef.current.element.removeEventListener('gmp-error', autocompleteRef.current.handleAutocompleteError);
                if (autocompleteContainerRef.current)
                    autocompleteContainerRef.current.innerHTML = '';
                autocompleteRef.current = null;
            }
        };
    }, [isLoaded, activeMode, addressKey, countryKey, stateKey, cityKey, zipKey, prefix]);

    // This effect runs when the selected country changes.
    useEffect(() => {
        let cancelled = false;
        if (selectedCountry) {
            setUseManualState(false);
            if (!autoFilled)
                setUseManualCity(false);

            if (selectedCountry === 'United States') {
                setStates(US_STATES.map(s => s.name));
                setIsLoadingStates(false);
            } else if (addressSubdivisions(selectedCountry).length) {
                setStates(addressSubdivisions(selectedCountry).map(region => region.name));
                setIsLoadingStates(false);
            } else {
                setIsLoadingStates(true);
                setStates([]);
                fetchStatesForCountry(selectedCountry)
                    .then(data => {
                        if (cancelled) return;
                        setStates(data);
                        setIsLoadingStates(false);
                    })
                    .catch(() => {
                        if (cancelled) return;
                        setIsLoadingStates(false);
                    });
            }
        } else {
            setStates([]);
            setCities([]);
        }
        return () => { cancelled = true; };
    }, [selectedCountry]);

    // This effect runs when the selected state changes
    useEffect(() => {
        let cancelled = false;
        if (selectedCountry && selectedState && selectedState !== 'Manual Entry') {
            if (!autoFilled)
                setUseManualCity(false);

            setIsLoadingCities(true);
            setCities([]);

            fetchCitiesForState(selectedCountry, selectedState)
                .then(data => {
                    if (cancelled) return;
                    setCities(data);
                    setIsLoadingCities(false);
                })
                .catch(() => {
                    if (cancelled) return;
                    setIsLoadingCities(false);
                });
        } else {
            setCities([]);
        }
        return () => { cancelled = true; };
    }, [selectedCountry, selectedState]);

    const isDark = theme === 'dark';
    const inputCls = `w-full border rounded-lg p-3 focus:ring-2 focus:outline-none transition-all ${
        isDark
            ? 'bg-slate-900/50 border-slate-600 text-white focus:ring-blue-500 placeholder-slate-500 disabled:bg-slate-800'
            : 'bg-white border-slate-200 text-slate-800 focus:ring-blue-500'
    }`;
    const labelCls = `block text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`;

    return (
        <div className="space-y-3">
            {/* Single instructional header — replaces the three competing labels */}
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Choose a saved asset location, search for a place, or enter an address manually.
            </p>

            <div className="flex flex-wrap p-1 bg-slate-800/50 rounded-lg border border-slate-700/50 w-fit">
                {assetLocations.length > 0 && <button
                    type="button"
                    onClick={() => setMode('assets')}
                    className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
                        activeMode === 'assets'
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                            : `${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`
                    }`}
                >
                    Asset Locations
                </button>}
                <button
                    type="button"
                    onClick={() => { setMode('search'); setSelectedAssetId(''); setAutoFilled(false); }}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
                        activeMode === 'search'
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                            : `${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`
                    }`}
                >
                    {/* Search icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                    Quick Search
                </button>
                <button
                    type="button"
                    onClick={() => { setMode('manual'); setSelectedAssetId(''); setAutoFilled(false); }}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
                        activeMode === 'manual'
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                            : `${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`
                    }`}
                >
                    {/* Pin icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Enter Manually
                </button>
            </div>

            {activeMode === 'assets' && (
                <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                    <label htmlFor={assetSelectId} className={labelCls}>Saved asset location</label>
                    <select
                        id={assetSelectId}
                        value={selectedAssetId}
                        onChange={event => {
                            const asset = assetLocations.find(item => item.id === event.target.value);
                            if (!asset)
                                return;
                            setSelectedAssetId(asset.id);
                            const fields = assetLocationFields(asset, prefix);
                            for (const [name, value] of Object.entries(fields))
                                onFormChange({ target: { name, value } });
                            setAutoFilled(true);
                            setUseManualState(false);
                            setUseManualCity(false);
                        }}
                        className={inputCls}
                    >
                        <option value="">Select an asset location</option>
                        {assetLocations.map(asset => <option key={asset.id} value={asset.id}>
                            {asset.name} ({asset.type}) — {asset.location?.address}
                        </option>)}
                    </select>
                    <p className={`mt-2 text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Copies the saved address once. Edit it below for this form.
                    </p>
                </div>
            )}
            {activeMode === 'search' && (
                <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                    {/* PlaceAutocompleteElement mounts here as a Web Component — no redundant label */}
                    <div
                        ref={autocompleteContainerRef}
                        className={`w-full rounded-lg border ${
                            isDark
                                ? 'border-slate-600 [&_gmp-placeautocomplete]:bg-slate-900'
                                : 'border-slate-200'
                        }`}
                        style={{ minHeight: '48px' }}
                    />
                    {!isLoaded && !loadError && (
                        <p className={`mt-2 text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            Loading destination search...
                        </p>
                    )}
                    {(loadError || autocompleteError) && (
                        <p className="mt-2 text-[11px] font-semibold text-amber-600">
                            {autocompleteError || 'Destination search could not load. Please enter the location manually.'}
                        </p>
                    )}
                    <p className={`mt-2 text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Choose a suggested result to fill the editable fields below.
                    </p>
                </div>
            )}
            <div className="animate-in fade-in slide-in-from-top-1 duration-300 space-y-4">
                    {/* Confirmation pill after a saved asset or place fills the form */}
                    {autoFilled && (formData[cityKey] || formData[addressKey]) && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            <p className="text-[11px] font-semibold text-emerald-400">
                                Location set: {selectedAssetId ? formData[addressKey] : [formData[cityKey] || formData[addressKey], formData[countryKey]].filter(Boolean).join(', ')}
                            </p>
                            <span className="ml-auto text-[10px] text-slate-500">Review or edit below</span>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Country */}
                        <div>
                            <label htmlFor={countryKey} className={labelCls}>Country</label>
                            <select
                                id={countryKey}
                                name={countryKey} {...geographicCountryWarning}
                                value={selectedCountry}
                                onChange={event => {
                                    onFormChange(event);
                                    onFormChange({ target: { name: stateKey, value: '' } });
                                    onFormChange({ target: { name: cityKey, value: '' } });
                                    setAutoFilled(false);
                                }}
                                className={inputCls}
                            >
                                <option value="">Select Country</option>
                                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>

                        {/* State / Region */}
                        <div>
                            <label htmlFor={stateKey} className={labelCls}>
                                State / Region {useManualState && <span className="text-blue-500 normal-case ml-1">(Manual)</span>}
                            </label>
                            {useManualState || (states.length === 0 && !isLoadingStates && selectedCountry) ? (
                                <GeographicInput
                                    type="text"
                                    id={stateKey}
                                    name={stateKey}
                                    value={selectedState || ''}
                                    onChange={onFormChange}
                                    placeholder="Enter State/Region"
                                    className={inputCls}
                                />
                            ) : (
                                <select
                                    id={stateKey}
                                    name={stateKey}
                                    value={selectedState}
                                    onChange={(e) => {
                                        if (e.target.value === 'Manual Entry') {
                                            setUseManualState(true);
                                            onFormChange({ target: { name: stateKey, value: '' } });
                                        } else {
                                            onFormChange(e);
                                        }
                                        onFormChange({ target: { name: cityKey, value: '' } });
                                    }}
                                    disabled={!selectedCountry || isLoadingStates}
                                    className={inputCls}
                                >
                                    {selectedState && !states.includes(selectedState) && <option value={selectedState}>{selectedState}</option>}
                                    {isLoadingStates ? (
                                        <option>Loading states...</option>
                                    ) : (
                                        <>
                                            <option value="">Select State/Region</option>
                                            {states.map(s => <option key={s} value={s}>{s}</option>)}
                                            <option value="Manual Entry">+ Type manually</option>
                                        </>
                                    )}
                                </select>
                            )}
                        </div>

                        {/* City */}
                        <div>
                            <label htmlFor={cityKey} className={labelCls}>
                                City {useManualCity && <span className="text-blue-500 normal-case ml-1">(Manual)</span>}
                            </label>
                            {useManualCity || (cities.length === 0 && !isLoadingCities && selectedState) ? (
                                <GeographicInput
                                    type="text"
                                    id={cityKey}
                                    name={cityKey}
                                    value={formData[cityKey] || ''}
                                    onChange={onFormChange}
                                    placeholder="Enter City"
                                    className={inputCls}
                                />
                            ) : (
                                <select
                                    id={cityKey}
                                    name={cityKey}
                                    value={formData[cityKey]}
                                    onChange={(e) => {
                                        if (e.target.value === 'Manual Entry') {
                                            setUseManualCity(true);
                                            onFormChange({ target: { name: cityKey, value: '' } });
                                        } else {
                                            onFormChange(e);
                                        }
                                    }}
                                    disabled={!selectedState || isLoadingCities}
                                    className={inputCls}
                                >
                                    {formData[cityKey] && !cities.includes(formData[cityKey]) && <option value={formData[cityKey]}>{formData[cityKey]}</option>}
                                    {isLoadingCities ? (
                                        <option>Loading cities...</option>
                                    ) : (
                                        <>
                                            <option value="">Select City</option>
                                            {cities.map(c => <option key={c} value={c}>{c}</option>)}
                                            <option value="Manual Entry">+ Type manually</option>
                                        </>
                                    )}
                                </select>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Street Address */}
                        <div>
                            <label htmlFor={addressKey} className={labelCls}>Street Address</label>
                            <GeographicInput
                                type="text"
                                id={addressKey}
                                name={addressKey}
                                value={formData[addressKey] || ''}
                                onChange={onFormChange}
                                className={inputCls}
                                placeholder="e.g., 1600 Amphitheatre Pkwy"
                            />
                        </div>
                        {/* ZIP / Postal Code */}
                        <div>
                            <label htmlFor={zipKey} className={labelCls}>ZIP / Postal Code</label>
                            <GeographicInput
                                type="text"
                                id={zipKey}
                                name={zipKey}
                                value={formData[zipKey] || ''}
                                onChange={onFormChange}
                                className={inputCls}
                                placeholder="e.g., 94043"
                            />
                        </div>
                    </div>
            </div>
        </div>
    );
};

export default AddressSelector;
